import { createContext, FC, PropsWithChildren, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { pick } from 'lodash';
import DraggableChatContext from '../interfaces/DraggableChatContext';
import ChatConversation from '../interfaces/ChatConversation';
import { ChanSession, ChanGroupedSessions } from '../interfaces/ChatSession';
import Message from '../interfaces/Message';
import {
  sendChatMessage,
  getChanMessageHistoryBySession,
  getChanTaskStatus,
  resumeChatMessage,
  getNewSessionName,
  ChatResponse
} from '../services';
import { createUserMessage, createBotMessage } from '../utils/Message';
import { DEFAULT_PLACEHOLDERS } from '../constants/suggestions';
import useUser from '../../../hooks/useUser';

type RawApiMessage = {
  id: number;
  content: string | null;
  role: string;
  feedback: string | null;
  suggestion: string | null;
  created_at: string;
};

const DraggableChatContextValue = createContext<DraggableChatContext>({
  currentConversation: null,
  isInitializing: false,
  sessions: [],
  setSessions: () => { },
  activeSession: null,
  setActiveSession: () => { },
  isCreatingSession: false,
  createSessionError: null,
  chatTitle: 'Untitled',
  setChatTitle: () => { },
  view: 'chat',
  setView: () => { },
  inputPlaceholder: DEFAULT_PLACEHOLDERS.INITIAL,
  setInputPlaceholder: () => { },
  showFollowUp: false,
  setShowFollowUp: () => { },
  isMinimized: false,
  setIsMinimized: () => { },
  createSession: () => { },
  deleteSession: () => { },
  addUserMessage: () => { },
  resetChat: () => { },
  copyMessageToClipboard: () => { },
  currentTaskId: "",
  setCurrentTaskId: () => { },
  isTaskPaused: false,
  setIsTaskPaused: () => { },
  suggestionMessage: '',
  setSuggestionMessage: () => { },
  groupedSessions: {},
  setGroupedSessions: () => { },
  regenerateChat: () => { },
  dragPosition: { 'x': 0, 'y': 0, },
  setDragPosition: () => { },
});

export const DraggableChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const [conversations, setConversations] = useState<Record<string, ChatConversation>>({});
  const [sessions, setSessions] = useState<ChanSession[]>([]);
  const [activeSession, setActiveSession] = useState({ id: "temp-session", name: "Untitled", createdAt: "", updatedAt: "" });
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [createSessionError,] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState('Untitled');
  const [view, setView] = useState<'chat' | 'pastChats'>('chat');
  const [inputPlaceholder, setInputPlaceholder] = useState<string>(DEFAULT_PLACEHOLDERS.INITIAL);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState("");
  const [isTaskPaused, setIsTaskPaused] = useState(false);
  const [groupedSessions, setGroupedSessions] = useState<ChanGroupedSessions>({});
  const isTaskPausedRef = useRef(false);
  const currentTaskIdRef = useRef("");
  const [dragPosition, setDragPosition] = useState({ 'x': 0, 'y': 0 });

  const [messageHistory, setMessageHistory] = useState<RawApiMessage[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [isInitializing, setIsInitializing] = useState(false);
  const [suggestionMessage, setSuggestionMessage] = useState('');


  const { selectedBusiness, currentUser } = useUser();

  useEffect(() => {
    if (selectedBusiness) {
      setIsInitializing(true);
      setMessageHistory([]);
      setActiveSession({ id: "temp-session", name: "Untitled", createdAt: "", updatedAt: "" });
      setChatTitle("Untitled");
      setConversations({});
      setGroupedSessions({});
      setView('chat');
      isTaskPausedRef.current = false;
      setIsTaskPaused(isTaskPausedRef.current);
      currentTaskIdRef.current = "";
      setIsInitializing(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (selectedBusiness && activeSession?.id && activeSession.id !== "temp-session") {
      setIsInitializing(true);
      setIsHistoryLoading(true);
      getChanMessageHistoryBySession(pick(selectedBusiness, ['id', 'name']), activeSession.id)
        .then(res => {
          const allMessages: RawApiMessage[] = res.tasks?.flatMap((task: any) => task.messages || []) || [];
          // console.log('---Message:\n', JSON.stringify(allMessages, null, 2));
          if (allMessages.length > 0 && conversations[activeSession.id]?.loading) {
            const lastMsg = allMessages[allMessages.length - 1];
            if (!lastMsg.role.includes('human')) {
              allMessages.pop();
            }
          }
          setMessageHistory(allMessages || []);
          setConversations((prev) => {
            const activeId = activeSession.id;
            const currentConvo = prev[activeId];
            const tempConvo = prev["temp-session"];

            if (allMessages.length === 0 && tempConvo?.messages?.length > 0) {
              return {
                ...prev,
                [activeId]: { ...tempConvo },
                "temp-session": { messages: [], loading: false, error: null }
              };
            }

            return {
              ...prev,
              [activeId]: {
                ...(currentConvo || { messages: [], error: null }),
                messages: [],
                error: null,
              },
            };
          });
          const lastTask = res.tasks.length > 0 ? res.tasks[res.tasks.length - 1] : null;
          if (lastTask) {
            isTaskPausedRef.current = (lastTask.status === "paused");
            setIsTaskPaused(isTaskPausedRef.current);
            setCurrentTaskId(lastTask.id);
            currentTaskIdRef.current = lastTask.id;
          }

          setSuggestionMessage('')
          if (allMessages.length > 0 && allMessages[allMessages.length - 1].role === 'ai') {
            setShowFollowUp(true);
          }
        })
        .catch(error => {
          console.log("Message hist setting error: ", error);
          setMessageHistory([]);
        })
        .finally(() => {
          setIsInitializing(false);
          setIsHistoryLoading(false);
        });
    } else {
      setMessageHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, selectedBusiness]);

  const currentConversation = useMemo((): ChatConversation | null => {
    if (!activeSession?.id) return null;

    const historyMessages: Message[] = activeSession.id === "temp-session"
      ? []
      : messageHistory.map((msg: RawApiMessage) => {
        const content = msg.content ?? "";
        const sender = msg.role;
        const text = content;
        const suggestedCustomizations: string[] | undefined = undefined;

        return {
          id: `hist_${msg.id}`,
          text,
          sender,
          timestamp: new Date(msg.created_at),
          suggestedCustomizations,
          feedback: msg.feedback,
        };
      });

    const newMessages = (activeSession?.id && conversations[activeSession.id]?.messages) || [];

    const finalMessages = [...historyMessages, ...newMessages]
    return {
      messages: finalMessages,
      loading: isHistoryLoading || (conversations[activeSession.id]?.loading ?? false) || (finalMessages[finalMessages.length - 1]?.sender.includes("human") && !conversations[activeSession.id]?.error),
      error: conversations[activeSession.id]?.error || null,
    };
  }, [activeSession?.id, messageHistory, conversations, isHistoryLoading]);

  const handleCreateSession = useCallback(() => {
    setIsCreatingSession(true);
    setActiveSession({ id: "temp-session", name: "Untitled", createdAt: "", updatedAt: "" });
    setChatTitle("Untitled");
    setConversations(prev => ({
      ...prev,
      "temp-session": { messages: [], loading: false, error: null },
    }));
    setIsCreatingSession(false);
    setView('chat');
    isTaskPausedRef.current = false;
    setIsTaskPaused(isTaskPausedRef.current);
    currentTaskIdRef.current = "";
    setInputPlaceholder(DEFAULT_PLACEHOLDERS.INITIAL);
  }, []);

  const handleDeleteSession = useCallback((session: ChanSession) => {
    setSessions(prev => prev.filter(s => s.id !== session.id));
    setConversations(prev => {
      const updated = { ...prev };
      delete updated[session.id];
      return updated;
    });
    if (activeSession.id === session.id) {
      setActiveSession({ id: "temp-session", name: "Untitled", createdAt: "", updatedAt: "" });
    }
  }, [activeSession]);

  const pollBotResponseStatus = async (botResponse: any, poll_interval = 5000): Promise<any> => {
    const POLL_INTERVAL_MS = poll_interval;
    const SECOND_TO_MS = 1000
    const INITIAL_DELAY = 10 * SECOND_TO_MS
    const MAX_DURATION_MS = 180 * SECOND_TO_MS;
    const MAX_ATTEMPTS = MAX_DURATION_MS / POLL_INTERVAL_MS;

    let response;
    let attempts = 0;

    // initial delay
    await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY));

    for (;;) {
      response = await getChanTaskStatus(botResponse.taskId);
      if (response?.status === "completed" || response?.status === "failed" || response?.status === "paused") {
        return response;
      }
      attempts++;

      if (attempts >= MAX_ATTEMPTS) {
        return { ...response, status: "timeout", };
      }

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

    }
  };


  const handleAddUserMessage = useCallback(async (sessionId: string, messageText: string) => {
    if (!messageText.trim() || !activeSession) return;

    const sender = isTaskPausedRef.current ? "human_feedback" : "human";
    const userMessage = createUserMessage(messageText, sender);

    try {
      console.log("--Session_id ---", sessionId)
      setConversations(prev => ({
        ...prev,
        [sessionId]: { ...prev[sessionId], messages: [...(prev[sessionId]?.messages || []), userMessage], loading: true, error: null },
      }));
      setShowFollowUp(false);

      let botResponse: ChatResponse = { taskId: "", message: "", sessionId: "", sessionName: "", status: "" };

      if (!isTaskPausedRef.current) {
        botResponse = await sendChatMessage(
          currentUser.tenant.esKey,
          messageText,
          activeSession.id === "temp-session" ? "" : activeSession.id,
          pick(selectedBusiness, ['id', 'name'])
        );
      } else {
        botResponse = await resumeChatMessage(sessionId, messageText, currentTaskIdRef.current, selectedBusiness.name, pick(selectedBusiness, ['id', 'name']));
      }

      setGroupedSessions({})
      if (sessionId === "temp-session") {
        const newSession = { id: botResponse.sessionId, name: "Untitled", createdAt: "", updatedAt: "" };
        setSessions(prev => [...prev, newSession]);
        setActiveSession(newSession);

        getNewSessionName(botResponse.sessionId, messageText)
          .then((response: any) => {
              if (!response?.sessionName) return;
              setChatTitle(response.sessionName);
              setSessions(prev =>
                  prev.map(session =>
                      session.id === botResponse.sessionId
                          ? { ...session, name: response.sessionName }
                          : session
                  )
              );
          })
          .catch(err =>
              console.log("Session title generation failed", err)
          );
      }

      const processedResponse = await pollBotResponseStatus(botResponse)

      const paused = processedResponse.status === "paused";
      isTaskPausedRef.current = paused;
      setIsTaskPaused(paused);
      setCurrentTaskId(botResponse.taskId);
      currentTaskIdRef.current = botResponse.taskId;

      const responseText = processedResponse?.content || "I'm sorry, I didn't receive a response. Please try again.";
      const botMessage = createBotMessage(responseText, [], processedResponse?.messageId);

      setConversations(prev => ({
        ...prev,
        [botResponse?.sessionId]: { ...prev[botResponse?.sessionId], messages: [...(prev[botResponse?.sessionId]?.messages || []), botMessage], loading: false },
      }));
      setInputPlaceholder(DEFAULT_PLACEHOLDERS.FOLLOWUP);
      setShowFollowUp(true);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      setConversations(prev => ({
        ...prev, [sessionId]: { ...prev[sessionId], loading: false, error: errorMessage },
      }));
    }
  }, [activeSession, currentUser.tenant.esKey, selectedBusiness,]);

  const handleResetChat = useCallback(() => {
    handleCreateSession();
  }, [handleCreateSession]);

  const handleCopyToClipboard = useCallback(async (text: string) => {
    toast.dismiss();
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Message copied to clipboard!');
    } catch (error) {
      toast.error('Could not copy text to clipboard.');
      console.error('Clipboard copy failed:', error);
    }
  }, []);

  const handleRegenerateChat = useCallback(async () => {
    toast.dismiss();
    try {
      const messages = currentConversation?.messages || [];
      const lastUserMessage = messages.at(-1)?.text ?? "";

      if (!lastUserMessage) {
        toast.error("No message found to regenerate.");
        return;
      }
      setConversations(prev => ({
        ...prev,
        [activeSession?.id]: { ...prev[activeSession?.id], messages: [], loading: false, error: null },
      }));
      await handleAddUserMessage(activeSession?.id ?? "", lastUserMessage);
    } catch (error) {
      toast.error('Failed to regenerate chat!');
      console.error('Regenerate Chat failed: ', error);
    }
  }, [currentConversation, activeSession, handleAddUserMessage])

  const contextValue: DraggableChatContext = {
    currentConversation,
    isInitializing,
    sessions,
    setSessions,
    activeSession,
    setActiveSession,
    isCreatingSession,
    createSessionError,
    chatTitle,
    setChatTitle,
    view,
    setView,
    inputPlaceholder,
    setInputPlaceholder,
    showFollowUp,
    setShowFollowUp,
    isMinimized,
    setIsMinimized,
    createSession: handleCreateSession,
    deleteSession: handleDeleteSession,
    addUserMessage: handleAddUserMessage,
    resetChat: handleResetChat,
    copyMessageToClipboard: handleCopyToClipboard,
    currentTaskId,
    setCurrentTaskId,
    isTaskPaused,
    setIsTaskPaused,
    suggestionMessage,
    setSuggestionMessage,
    groupedSessions,
    setGroupedSessions,
    regenerateChat: handleRegenerateChat,
    dragPosition,
    setDragPosition,
  };

  return <DraggableChatContextValue.Provider value={contextValue}>{children}</DraggableChatContextValue.Provider>;
};

export default DraggableChatContextValue;